import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Search, Filter, Users, UserPlus, AlertTriangle, 
  CheckCircle, Loader2, RefreshCw 
} from 'lucide-react';
import { 
  usePetsForAssignment, 
  useActiveProviders, 
  useBulkAssignProvider,
  PetForAssignment 
} from '@/hooks/useNutritionAdmin';
import { format } from 'date-fns';
import { es, enUS, de, fr, pt, it, ja, ko, ru, zhCN } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';

const getDateLocale = (lng: string) => {
  const locales: Record<string, typeof es> = {
    es, en: enUS, de, fr, pt, it, ja, ko, ru, zh: zhCN
  };
  return locales[lng] || enUS;
};

const NutritionProviderAssignment: React.FC = () => {
  const { t, i18n } = useTranslation();
  
  // Filters state
  const [brandFilter, setBrandFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [foodTypeFilter, setFoodTypeFilter] = useState<string>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  
  // Selection state
  const [selectedPets, setSelectedPets] = useState<Set<string>>(new Set());
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  
  // Determine hasProvider filter value
  const hasProviderValue = providerFilter === 'with' ? true : providerFilter === 'without' ? false : null;
  
  // Queries
  const { data: pets, isLoading, refetch, isFetching } = usePetsForAssignment({
    brand: brandFilter || undefined,
    city: cityFilter || undefined,
    foodType: foodTypeFilter === 'all' ? undefined : foodTypeFilter,
    hasProvider: hasProviderValue,
    limit: 100,
  });
  
  const { data: providers } = useActiveProviders();
  const bulkAssign = useBulkAssignProvider();

  const handleSelectAll = (checked: boolean) => {
    if (checked && pets) {
      setSelectedPets(new Set(pets.map(p => p.pet_id)));
    } else {
      setSelectedPets(new Set());
    }
  };

  const handleSelectPet = (petId: string, checked: boolean) => {
    const newSelected = new Set(selectedPets);
    if (checked) {
      newSelected.add(petId);
    } else {
      newSelected.delete(petId);
    }
    setSelectedPets(newSelected);
  };

  const handleBulkAssign = async () => {
    if (selectedPets.size === 0 || !selectedProvider) return;
    
    await bulkAssign.mutateAsync({
      petIds: Array.from(selectedPets),
      providerId: selectedProvider,
    });
    
    setSelectedPets(new Set());
    setSelectedProvider('');
  };

  const clearFilters = () => {
    setBrandFilter('');
    setCityFilter('');
    setFoodTypeFilter('all');
    setProviderFilter('all');
  };

  const getFoodTypeLabel = (type: string) => {
    const key = `nutritionAdmin.foodTypes.${type}`;
    const translated = t(key);
    return translated !== key ? translated : type;
  };

  return (
    <div className="space-y-6">
      {/* Filters Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            {t('nutritionAdmin.assignment.filtersTitle')}
          </CardTitle>
          <CardDescription>
            {t('nutritionAdmin.assignment.filtersDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Brand Filter */}
            <div className="space-y-2">
              <Label htmlFor="brand-filter">{t('nutritionAdmin.assignment.brand')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="brand-filter"
                  placeholder={t('nutritionAdmin.assignment.searchBrand')}
                  value={brandFilter}
                  onChange={(e) => setBrandFilter(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* City Filter */}
            <div className="space-y-2">
              <Label htmlFor="city-filter">{t('nutritionAdmin.assignment.city')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="city-filter"
                  placeholder={t('nutritionAdmin.assignment.searchCity')}
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Food Type Filter */}
            <div className="space-y-2">
              <Label>{t('nutritionAdmin.assignment.dietType')}</Label>
              <Select value={foodTypeFilter} onValueChange={setFoodTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder={t('nutritionAdmin.assignment.allDiets')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('nutritionAdmin.assignment.allDiets')}</SelectItem>
                  <SelectItem value="dry_kibble">{t('nutritionAdmin.foodTypes.dry_kibble')}</SelectItem>
                  <SelectItem value="wet_food">{t('nutritionAdmin.foodTypes.wet_food')}</SelectItem>
                  <SelectItem value="barf">{t('nutritionAdmin.foodTypes.barf')}</SelectItem>
                  <SelectItem value="homemade">{t('nutritionAdmin.foodTypes.homemade')}</SelectItem>
                  <SelectItem value="prescription">{t('nutritionAdmin.foodTypes.prescription')}</SelectItem>
                  <SelectItem value="grass">{t('nutritionAdmin.foodTypes.grass')}</SelectItem>
                  <SelectItem value="hay">{t('nutritionAdmin.foodTypes.hay')}</SelectItem>
                  <SelectItem value="forage">{t('nutritionAdmin.foodTypes.forage')}</SelectItem>
                  <SelectItem value="herbs">{t('nutritionAdmin.foodTypes.herbs')}</SelectItem>
                  <SelectItem value="silage">{t('nutritionAdmin.foodTypes.silage')}</SelectItem>
                  <SelectItem value="concentrate">{t('nutritionAdmin.foodTypes.concentrate')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Provider Status Filter */}
            <div className="space-y-2">
              <Label>{t('nutritionAdmin.assignment.providerStatus')}</Label>
              <Select value={providerFilter} onValueChange={setProviderFilter}>
                <SelectTrigger>
                  <SelectValue placeholder={t('nutritionAdmin.assignment.allProviders')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('nutritionAdmin.assignment.allProviders')}</SelectItem>
                  <SelectItem value="without">{t('nutritionAdmin.assignment.withoutProvider')}</SelectItem>
                  <SelectItem value="with">{t('nutritionAdmin.assignment.withProvider')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={clearFilters} size="sm">
              {t('nutritionAdmin.assignment.clearFilters')}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => refetch()} 
              size="sm"
              disabled={isFetching}
            >
              {isFetching ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {t('common.refresh')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Action Card */}
      {selectedPets.size > 0 && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                <span className="font-medium">
                  {t('nutritionAdmin.assignment.petsSelected', { count: selectedPets.size })}
                </span>
              </div>
              
              <div className="flex-1 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                  <SelectTrigger className="w-full sm:w-[300px]">
                    <SelectValue placeholder={t('nutritionAdmin.assignment.selectProvider')} />
                  </SelectTrigger>
                  <SelectContent>
                    {providers?.map((provider) => (
                      <SelectItem key={provider.id} value={provider.id}>
                        <div className="flex items-center gap-2">
                          {provider.verified && (
                            <CheckCircle className="h-3 w-3 text-green-500" />
                          )}
                          <span>{provider.business_name}</span>
                          <span className="text-muted-foreground text-xs">
                            ({provider.city || t('nutritionAdmin.assignment.noCity')})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button 
                  onClick={handleBulkAssign}
                  disabled={!selectedProvider || bulkAssign.isPending}
                >
                  {bulkAssign.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <UserPlus className="h-4 w-4 mr-2" />
                  )}
                  {t('nutritionAdmin.assignment.assignToSelected')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t('nutritionAdmin.assignment.petsWithNutrition')}
            </span>
            {pets && (
              <Badge variant="secondary">
                {t('nutritionAdmin.assignment.results', { count: pets.length })}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : pets && pets.length > 0 ? (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <Checkbox
                        checked={pets.length > 0 && selectedPets.size === pets.length}
                        onCheckedChange={handleSelectAll}
                      />
                    </TableHead>
                    <TableHead>{t('nutritionAdmin.assignment.pet')}</TableHead>
                    <TableHead>{t('nutritionAdmin.assignment.speciesBreed')}</TableHead>
                    <TableHead>{t('nutritionAdmin.assignment.owner')}</TableHead>
                    <TableHead>{t('nutritionAdmin.assignment.city')}</TableHead>
                    <TableHead>{t('nutritionAdmin.assignment.diet')}</TableHead>
                    <TableHead>{t('nutritionAdmin.assignment.provider')}</TableHead>
                    <TableHead>{t('nutritionAdmin.assignment.lastUpdate')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pets.map((pet) => (
                    <TableRow 
                      key={pet.pet_id}
                      className={selectedPets.has(pet.pet_id) ? 'bg-primary/5' : ''}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedPets.has(pet.pet_id)}
                          onCheckedChange={(checked) => 
                            handleSelectPet(pet.pet_id, checked as boolean)
                          }
                        />
                      </TableCell>
                      <TableCell className="font-medium">{pet.pet_name}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{pet.species || 'N/A'}</div>
                          <div className="text-muted-foreground text-xs">{pet.breed || t('nutritionAdmin.assignment.noBreed')}</div>
                        </div>
                      </TableCell>
                      <TableCell>{pet.owner_name || 'N/A'}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{pet.city || t('nutritionAdmin.assignment.noCity')}</div>
                          {pet.state && (
                            <div className="text-muted-foreground text-xs">{pet.state}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <Badge 
                            variant={pet.food_type === 'prescription' ? 'destructive' : 'outline'}
                            className="text-xs"
                          >
                            {getFoodTypeLabel(pet.food_type)}
                          </Badge>
                          {pet.brand && (
                            <div className="text-xs text-muted-foreground">{pet.brand}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {pet.provider_name ? (
                          <Badge variant="secondary" className="flex items-center gap-1">
                            <CheckCircle className="h-3 w-3 text-green-500" />
                            {pet.provider_name}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-amber-600 border-amber-300">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {t('nutritionAdmin.assignment.unassigned')}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {pet.diet_updated_at 
                          ? format(new Date(pet.diet_updated_at), 'dd MMM yyyy', { locale: getDateLocale(i18n.language) })
                          : 'N/A'
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('nutritionAdmin.assignment.noPetsFound')}</p>
              <Button variant="link" onClick={clearFilters}>
                {t('nutritionAdmin.assignment.clearFilters')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NutritionProviderAssignment;
